import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ConfigService } from '../../../services/config.service';
import { OauthService } from '../../../services/oauth.service';
import { LocalStorageUtil } from '../../../utils/local-storage.util';
import { tokenHasRole } from '../../../utils/jwt-role.util';
import { PendingMessageService } from '../../../services/pending-message.service';
import { InternalAuthFlowUtil } from '../../../utils/internal-auth-flow.util';

type OauthTrustDecision = {
  allowed: boolean;
  reason: string;
  path: 'dev' | 'sso-externo' | 'interno-referrer' | 'interno-state' | 'bloqueio';
};

@Component({
  selector: 'app-integration-digital-oauth',
  standalone: true,
  templateUrl: './oauth.html',
  styleUrls: ['./oauth.scss']
})
export class OauthDigitalIntegration implements OnInit {
  private readonly ssoAllowedHosts = ['bancovw.com.br'];
  private readonly internalFlowStateTtlMs = 5 * 60 * 1000;

  /** Logo e label conforme query param 'screen' (reports vs leads). */
  logoPath = 'assets/icons/titleLogoLeads.svg';
  logoAlt = 'Plataforma de Leads';

  constructor(
    private route: ActivatedRoute,
    private oauthService: OauthService,
    private configService: ConfigService,
    private router: Router,
    private location: Location,
    private pendingMessage: PendingMessageService) {}
  
  ngOnInit(): void {
    this.updateLogoFromParams(this.route.snapshot.queryParamMap);
    this.route.queryParamMap.subscribe(params => {
      this.updateLogoFromParams(params);
      const token = params.get('token');
      const screen = params.get('screen');
      const dn = params.get('dn');
      const username = params.get('username');
      const state = params.get('state');

      if (token) {
        if (username && username.trim().length > 0) {
          LocalStorageUtil.setLoginUsername(username.trim());
        }

        const trustDecision = this.evaluateOauthTrust(token, state);
        console.log('[OAuth][decision]', trustDecision);

        if (!trustDecision.allowed) {
          console.warn(`⚠️ Acesso ao /oauth bloqueado: ${trustDecision.reason}`);
          InternalAuthFlowUtil.clearState();
          LocalStorageUtil.clearAllVWData();
          this.redirectToInternalLogin();
          return;
        }

        // Remove token da URL imediatamente (mitigação RIS 180/179 - session token in URL)
        this.location.replaceState(this.router.url.split('?')[0] || '/oauth');

        this.configService.loadPublicConfig().pipe(take(1)).subscribe({
          next: () => {
            this.oauthService.oauth(token).subscribe({
              next: (user) => {
                LocalStorageUtil.setVWUser(user);
                LocalStorageUtil.setAccessToken(token);
                LocalStorageUtil.setSsoAuthenticated();

                if (dn && dn.trim().length > 0) {
                  LocalStorageUtil.setSelectedVWDn(dn);
                } else {
                  // Seleciona o primeiro DN da primeira concessionária da primeira montadora
                  if (user?.montadoras?.length > 0 &&
                      user.montadoras[0]?.concessionarias?.length > 0) {
                    const firstDn = user.montadoras[0].concessionarias[0].codigo;
                    LocalStorageUtil.setSelectedVWDn(firstDn);
                  }
                }

                // Carrega configuração do BFF após autenticação (com token no Bearer via interceptor)
                this.configService.loadConfig().pipe(take(1)).subscribe({
                  next: () => {
                    this.navigateByAllowedRole(token, screen);
                  },
                  error: (err) => {
                    console.error('❌ Erro ao carregar configuração:', err);
                    this.router.navigate(['/error'], {
                      state: { message: err?.message || 'Falha ao carregar configuração do sistema' },
                    });
                  },
                });
              },
              error: (error) => {
                console.error('❌ Erro ao carregar dados do usuario:', error);
                InternalAuthFlowUtil.clearState();
                LocalStorageUtil.clearAllVWData();

                this.router.navigate(['/error'], {
                  state: {message: error.message || 'Erro na comunicação com o sistema'}
                });
              }
            });
          },
          error: (error) => {
            console.error('❌ Erro ao carregar configuração pública:', error);
            InternalAuthFlowUtil.clearState();
            LocalStorageUtil.clearAllVWData();

            this.router.navigate(['/error'], {
              state: { message: error?.message || 'Falha ao carregar configuração pública do sistema' }
            });
          }
        });
        return;
      }

      if (LocalStorageUtil.hasAuthenticatedSession()) {
        const token = LocalStorageUtil.getAccessTokenOrDefault('');

        this.configService.loadConfig().pipe(take(1)).subscribe({
          next: () => {
            this.navigateByAllowedRole(token, screen);
          },
          error: () => {
            LocalStorageUtil.clearAllVWData();
            this.redirectToInternalLogin();
          }
        });
        return;
      }

      LocalStorageUtil.clearAllVWData();
      InternalAuthFlowUtil.clearState();
      this.redirectToInternalLogin();
    });
  }

  private updateLogoFromParams(params: { get: (k: string) => string | null }): void {
    const screen = params.get('screen');
    if (screen === 'reports') {
      this.logoPath = 'assets/icons/titleLogoReports.svg';
      this.logoAlt = 'Plataforma de Reports';
    } else {
      this.logoPath = 'assets/icons/titleLogoLeads.svg';
      this.logoAlt = 'Plataforma de Leads';
    }
  }

  private redirectToInternalLogin(): void {
    this.router.navigate(['/login']);
  }

  private redirectToLoginWithMessage(message: string): void {
    LocalStorageUtil.clearAllVWData();
    this.router.navigate(['/login'], {
      queryParams: { authMessage: message }
    });
  }

  private navigateByAllowedRole(token: string, preferredScreen: string | null): void {
    const hasReportsRole = tokenHasRole(token, 'Reports');
    const hasLeadsRole = tokenHasRole(token, 'Leads');

    if (preferredScreen === 'reports') {
      if (hasReportsRole) {
        this.router.navigate(['/reports']);
        return;
      }

      if (hasLeadsRole) {
        this.router.navigate(['/dashboard']);
        return;
      }
    } else {
      if (hasLeadsRole) {
        this.router.navigate(['/dashboard']);
        return;
      }

      // Só redireciona para /reports se phase2 está habilitado
      if (hasReportsRole && this.configService.isPhase2Enabled()) {
        this.pendingMessage.set('Usuário sem permissão de acesso na área Leads.');
        this.router.navigate(['/reports']);
        return;
      }
    }

    if (preferredScreen === 'reports') {
      this.redirectToLoginWithMessage('Usuário sem permissão de acesso na Plataforma de Leads.');
      return;
    }

    this.redirectToLoginWithMessage('Usuário sem permissão de acesso na Plataforma de Leads.');
  }

  private evaluateOauthTrust(token: string, state: string | null): OauthTrustDecision {
    const currentHostname = window.location.hostname.toLowerCase();
    const referrer = document.referrer;
    const hasTokenInRoute = token.trim().length > 0;

    console.log('[OAuth][canProcess] currentHostname:', currentHostname);
    console.log('[OAuth][canProcess] document.referrer:', referrer || '(vazio)');
    console.log('[OAuth][canProcess] hasTokenInRoute:', hasTokenInRoute);

    if (this.isDevHostname(currentHostname)) {
      return { allowed: true, reason: 'ambiente de desenvolvimento', path: 'dev' };
    }

    const validInternalState = InternalAuthFlowUtil.consumeState(state, this.internalFlowStateTtlMs);
    console.log('[OAuth][canProcess] internalState:', validInternalState.reason);

    if (!referrer) {
      if (validInternalState.isValid) {
        return {
          allowed: true,
          reason: 'referrer vazio com prova valida de fluxo interno',
          path: 'interno-state'
        };
      }

      return {
        allowed: false,
        reason: `referrer vazio sem prova valida de fluxo interno: ${validInternalState.reason}`,
        path: 'bloqueio'
      };
    }

    try {
      const referrerUrl = new URL(referrer);
      const referrerHost = referrerUrl.hostname.toLowerCase();
      const referrerPath = referrerUrl.pathname.toLowerCase();
      console.log('[OAuth][canProcess] referrerHost:', referrerHost);

      if (this.isSsoReferrerAllowed(referrerHost)) {
        return {
          allowed: true,
          reason: `referrer SSO permitido: ${referrerHost}`,
          path: 'sso-externo'
        };
      }

      if (this.isInternalLoginReferrer(referrerHost, currentHostname, referrerPath)) {
        if (validInternalState.isValid) {
          return {
            allowed: true,
            reason: `fluxo interno login -> oauth validado por referrer e state: ${referrerPath}`,
            path: 'interno-referrer'
          };
        }

        return {
          allowed: false,
          reason: `referrer interno detectado sem state confiavel: ${validInternalState.reason}`,
          path: 'bloqueio'
        };
      }

      return {
        allowed: false,
        reason: `referrer nao confiavel: ${referrerHost}${referrerPath}`,
        path: 'bloqueio'
      };
    } catch {
      console.log('[OAuth][canProcess] referrer inválido para parse');
      return {
        allowed: false,
        reason: 'referrer invalido para parse',
        path: 'bloqueio'
      };
    }
  }

  private isDevHostname(hostname: string): boolean {
    return hostname.includes('dev.productsportal.cld.bancovw.internal')
      || hostname === 'localhost'
      || hostname === '127.0.0.1';
  }

  private isSsoReferrerAllowed(referrerHost: string): boolean {
    return this.ssoAllowedHosts.some(
      allowedHost => referrerHost === allowedHost || referrerHost.endsWith(`.${allowedHost}`)
    );
  }

  private isInternalLoginReferrer(referrerHost: string, currentHostname: string, referrerPath: string): boolean {
    if (referrerHost !== currentHostname) {
      return false;
    }

    return referrerPath === '/login' || referrerPath.endsWith('/login');
  }
}

